import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	IconButton,
	TextField,
	Typography,
	CircularProgress,
	Stack,
	FormLabel,
} from '@mui/material';
import { useApi } from '../../../hooks/useApi';
import {
	useGlobalDispatch,
	useGlobalState,
} from '../../../stateProviders/GlobalStateProvider';
import { CompanyFinancingPlansUpdateParams } from '../../../types/api/Company';
import { TbTrash } from 'react-icons/tb';
import { useUnsavedFormChanges } from '../../../hooks/useUnsavedFormChanges';
import { useUnsavedChangesContext } from '../../../contexts/UnsavedChangesContext';

export const FinancingPlans = () => {
	const globalState = useGlobalState();
	const [financingPlans, setFinancingPlans] = useState(
		(globalState.company?.financingPlans || []).map((plan) => ({
			planName: plan.planName || '',
			paymentFactor: String(plan.paymentFactor || ''),
		}))
	);
	const [isSaving, setIsSaving] = useState(false);
	const api = useApi();
	const globalDispatch = useGlobalDispatch();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const planRefs = useRef<(HTMLDivElement | null)[]>([]);

	const handlePlanChange = (
		index: number,
		field: 'planName' | 'paymentFactor',
		value: string
	) => {
		const updatedPlans = [...financingPlans];
		updatedPlans[index] = {
			...updatedPlans[index],
			[field]: value,
		};
		setFinancingPlans(updatedPlans);
	};

	const addFinancingPlan = () => {
		const newPlans = [...financingPlans, { planName: '', paymentFactor: '' }];
		setFinancingPlans(newPlans);

		setTimeout(() => {
			const newPlanIndex = newPlans.length - 1;
			const newPlanElement = planRefs.current[newPlanIndex];
			if (newPlanElement && scrollContainerRef.current) {
				newPlanElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});

				const planNameInput = newPlanElement.querySelector(
					'input[name^="planName"]'
				) as HTMLInputElement;
				if (planNameInput) {
					planNameInput.focus();
				}
			}
		}, 100);
	};

	const removeFinancingPlan = async (index: number) => {
		const updatedPlans = financingPlans.filter((_, i) => i !== index);
		setFinancingPlans(updatedPlans);
		await updateCompanyFinancingPlans(undefined, updatedPlans);
	};

	useEffect(() => {
		planRefs.current = planRefs.current.slice(0, financingPlans.length);
	}, [financingPlans.length]);

	useEffect(() => {
		setFinancingPlans(
			(globalState.company?.financingPlans || []).map((plan) => ({
				planName: plan.planName || '',
				paymentFactor: String(plan.paymentFactor || ''),
			}))
		);
	}, [globalState.company?.financingPlans]);

	const savedFormData = useMemo(
		() => ({
			financingPlans: JSON.stringify(
				(globalState.company?.financingPlans || []).map((plan) => ({
					planName: plan.planName || '',
					paymentFactor: String(plan.paymentFactor || ''),
				}))
			),
		}),
		[globalState.company?.financingPlans]
	);

	const currentFormData = useMemo(
		() => ({
			financingPlans: JSON.stringify(financingPlans),
		}),
		[financingPlans]
	);

	const { isDirty, markAsClean } = useUnsavedFormChanges(
		currentFormData,
		savedFormData
	);
	const { setHasUnsavedChanges, setOnSaveCallback } =
		useUnsavedChangesContext();

	const updateCompanyFinancingPlans = async (
		event?: React.FormEvent<HTMLFormElement>,
		plansToSave?: { planName: string; paymentFactor: string }[]
	) => {
		if (event) event.preventDefault();

		if (!globalState.company?._id) return;

		const plansForAPI = (plansToSave || financingPlans).map((plan) => ({
			planName: plan.planName.trim(),
			paymentFactor: Number(plan.paymentFactor) || 0,
		}));

		try {
			setIsSaving(true);
			await api.company.updateFinancingPlans(globalState.company._id, {
				financingPlans: plansForAPI,
			} as CompanyFinancingPlansUpdateParams);
			const updatedCompany = await api.company.getCurrent();

			globalDispatch({ company: updatedCompany });
			markAsClean();
		} catch (error) {
			console.error(error);
		} finally {
			setIsSaving(false);
		}
	};

	useEffect(() => {
		setHasUnsavedChanges(isDirty);
	}, [isDirty, setHasUnsavedChanges]);

	useEffect(() => {
		if (isDirty) {
			setOnSaveCallback(() => async () => {
				await updateCompanyFinancingPlans();
			});
		} else {
			setOnSaveCallback(null);
		}
	}, [isDirty, financingPlans]);

	useEffect(() => {
		return () => {
			setHasUnsavedChanges(false);
			setOnSaveCallback(null);
		};
	}, [setHasUnsavedChanges, setOnSaveCallback]);

	return (
		<Box
			display="flex"
			flexDirection="column"
			maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }}
			component="form"
			onSubmit={updateCompanyFinancingPlans}
		>
			{/* Sticky Header */}
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					backgroundColor: 'background.default',
					zIndex: 10,
					pb: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 3,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					mb={2}
				>
					<Stack spacing={1}>
						<Typography variant="h2" fontWeight={700}>
							Financing Plans
						</Typography>
						<Typography variant="subtitle1" color="text.secondary">
							Create financing plans that can be added to quotes
						</Typography>
					</Stack>
					<Button
						variant="contained"
						color="success"
						onClick={addFinancingPlan}
					>
						Add New Plan
					</Button>
				</Stack>
			</Box>

			{/* Content */}
			<Box
				ref={scrollContainerRef}
				display="flex"
				flexDirection="column"
				gap={3}
			>
				{financingPlans.length === 0 && (
					<Typography variant="sm" color="text.secondary">
						No financial plans added yet. Use the button above to create one.
					</Typography>
				)}
				{financingPlans.map(({ planName, paymentFactor }, i) => (
					<Box
						key={i}
						ref={(el) => (planRefs.current[i] = el)}
						display="grid"
						gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
						gap={2}
					>
						{financingPlans.length ? (
							<React.Fragment>
								<Stack spacing={1}>
									<FormLabel htmlFor={`planName-${i}`}>Plan name</FormLabel>
									<TextField
										id={`planName-${i}`}
										name={`planName-${i}`}
										variant="outlined"
										value={planName}
										onChange={(e) =>
											handlePlanChange(i, 'planName', e.target.value)
										}
										disabled={isSaving}
										fullWidth
									/>
								</Stack>
								<Stack spacing={1}>
									<FormLabel htmlFor={`paymentFactor-${i}`}>
										Payment factor
									</FormLabel>
									<Stack direction="row" alignItems="center" gap={1}>
										<TextField
											id={`paymentFactor-${i}`}
											type="number"
											name={`paymentFactor-${i}`}
											variant="outlined"
											slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
											value={paymentFactor}
											onChange={(e) =>
												handlePlanChange(i, 'paymentFactor', e.target.value)
											}
											disabled={isSaving}
											fullWidth
										/>
										<IconButton
											color="error"
											onClick={() => removeFinancingPlan(i)}
											edge="end"
											disabled={isSaving}
											aria-label="Remove plan"
											sx={{ alignSelf: 'center' }}
										>
											<TbTrash />
										</IconButton>
									</Stack>
								</Stack>
							</React.Fragment>
						) : (
							<Typography
								variant="body1"
								color="text.secondary"
								sx={{ gridColumn: '1 / -1' }}
							>
								No financial plans added yet. Use the button below to create
								one.
							</Typography>
						)}
					</Box>
				))}
				<Button
					variant="contained"
					color="success"
					type="submit"
					disabled={isSaving}
					sx={{
						alignSelf: 'flex-start',
					}}
				>
					{isSaving ? (
						<Box display="flex" alignItems="center" gap={1}>
							<CircularProgress size={18} />
							<Typography variant="xs" component="span" fontWeight={600}>
								Saving...
							</Typography>
						</Box>
					) : (
						'Save Financing Plans'
					)}
				</Button>
			</Box>
		</Box>
	);
};
